import "@/App.css";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselPrevious,
  CarouselNext,
} from "@/components/ui/carousel";
import Autoplay from "embla-carousel-autoplay";
import { Phone, Mail, MapPin, Clock, Facebook, Instagram, Star } from "lucide-react";

// Portfolio images - self-hosted
const portfolioImages = [
  {
    url: `${process.env.PUBLIC_URL}/images/portfolio-1.jpg`,
    title: "Carusel, căluți, poveste",
  },
  {
    url: `${process.env.PUBLIC_URL}/images/portfolio-2.jpg`,
    title: "Trend rustic și botanic",
  },
  {
    url: `${process.env.PUBLIC_URL}/images/portfolio-3.jpg`,
    title: "The Great Gatsby",
  },
  {
    url: `${process.env.PUBLIC_URL}/images/portfolio-4.jpg`,
    title: "Magic Forest",
  },
  {
    url: `${process.env.PUBLIC_URL}/images/portfolio-5.jpg`,
    title: "Micuța Balerină",
  },
  {
    url: `${process.env.PUBLIC_URL}/images/portfolio-6.jpg`,
    title: "Mov și auriu",
  },
];

// Services data
const services = [
  {
    title: "Planificarea nunții",
    description:
      "Vă ajutăm să trăiți din plin bucuria momentelor frumoase, oferindu-vă servicii complete de organizare. Împreună vom crea nunta voastră de vis.",
    icon: "ring",
  },
  {
    title: "Organizare evenimente de familie",
    description:
      "Botezuri, majorate sau aniversări. Ne bucurăm de fiecare provocare și transformăm orice petrecere într-o amintire prețioasă.",
    icon: "heart",
  },
  {
    title: "Decorațiuni evenimente",
    description:
      "Elementele de decor spun povești și transformă visele în realitate. Împreună, putem crea decorul perfect.",
    icon: "sparkle",
  },
  {
    title: "Aranjamente florale",
    description:
      "Atelierul nostru creează aranjamente unice. Un decor este captivant atunci când îl asortezi cu florile potrivite.",
    icon: "flower",
  },
];

// Service Icon Component with aria-hidden for accessibility
const ServiceIcon = ({ type }) => {
  const iconClass = "w-8 h-8 text-[#2D5A27]";
  switch (type) {
    case "ring":
      return (
        <svg className={iconClass} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" aria-hidden="true">
          <circle cx="12" cy="12" r="8" />
          <circle cx="12" cy="12" r="3" />
          <path d="M12 4v2M12 18v2M4 12h2M18 12h2" />
        </svg>
      );
    case "heart":
      return (
        <svg className={iconClass} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" aria-hidden="true">
          <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z" />
        </svg>
      );
    case "sparkle":
      return (
        <svg className={iconClass} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" aria-hidden="true">
          <path d="M12 2l2.4 7.2L22 12l-7.6 2.8L12 22l-2.4-7.2L2 12l7.6-2.8L12 2z" />
        </svg>
      );
    case "flower":
      return (
        <svg className={iconClass} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" aria-hidden="true">
          <circle cx="12" cy="12" r="3" />
          <path d="M12 2a4 4 0 0 1 0 8 4 4 0 0 1 0-8M4.93 4.93a4 4 0 0 1 5.66 5.66 4 4 0 0 1-5.66-5.66M2 12a4 4 0 0 1 8 0 4 4 0 0 1-8 0M4.93 19.07a4 4 0 0 1 5.66-5.66 4 4 0 0 1-5.66 5.66M12 14a4 4 0 0 1 0 8 4 4 0 0 1 0-8M19.07 19.07a4 4 0 0 1-5.66-5.66 4 4 0 0 1 5.66 5.66M14 12a4 4 0 0 1 8 0 4 4 0 0 1-8 0M19.07 4.93a4 4 0 0 1-5.66 5.66 4 4 0 0 1 5.66-5.66" />
        </svg>
      );
    default:
      return <Star className={iconClass} aria-hidden="true" />;
  }
};

const LandingPage = () => {
  return (
    <div className="min-h-screen font-body" style={{ backgroundColor: "#FDFBF7" }} data-testid="landing-page">
      {/* Header */}
      <header className="py-6 px-6 md:px-12 lg:px-24" data-testid="header" role="banner">
        <div className="max-w-7xl mx-auto flex justify-center">
          <div className="logo-container" data-testid="logo-container">
            <img
              src={`${process.env.PUBLIC_URL}/images/logo.png`}
              alt="Star Events - Visele pot deveni realitate"
              className="h-20 md:h-28 w-auto"
              data-testid="logo"
              width="280"
              height="112"
              loading="eager"
            />
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main id="main-content" role="main">
        {/* Hero Section */}
        <section className="px-6 md:px-12 lg:px-24 py-16 md:py-24" data-testid="hero-section" aria-labelledby="hero-headline">
          <div className="max-w-4xl mx-auto text-center">
            {/* Under Construction Badge */}
            <div className="inline-block mb-8 animate-fade-in" data-testid="construction-badge">
              <span className="construction-badge inline-flex items-center gap-2 px-4 py-2 bg-[#2D5A27] text-white text-sm font-medium tracking-wider uppercase rounded" role="status">
                <span className="w-2 h-2 bg-white rounded-full animate-pulse" aria-hidden="true"></span>
                Site în construcție
              </span>
            </div>

            {/* Main Headline - WCAG: min 16px, contrast 14.5:1 */}
            <h1
              id="hero-headline"
              className="font-heading text-4xl sm:text-5xl lg:text-7xl font-light tracking-tight mb-6 animate-fade-in-up"
              style={{ color: "#1A231E" }}
              data-testid="hero-headline"
            >
              Creăm amintiri de neuitat.
            </h1>

            {/* Decorative Line */}
            <div className="decorative-line w-32 mx-auto my-8 animate-fade-in animation-delay-200" aria-hidden="true"></div>

            {/* Subheadline - WCAG: 16px min, contrast 7.2:1 with #4A524D */}
            <p
              className="text-base sm:text-lg max-w-2xl mx-auto mb-4 animate-fade-in-up animation-delay-400"
              style={{ color: "#4A524D" }}
              data-testid="hero-subheadline"
            >
              Noul nostru site este în construcție, dar pasiunea pentru evenimentele perfecte rămâne neschimbată.
            </p>

            <p
              className="text-base max-w-xl mx-auto animate-fade-in-up animation-delay-600"
              style={{ color: "#4A524D" }}
              data-testid="hero-description"
            >
              Pregătim o nouă experiență digitală. Între timp, continuăm să creăm nunți, botezuri și petreceri de vis.
            </p>
          </div>
        </section>

        {/* Portfolio Carousel */}
        <section className="py-16 md:py-24" data-testid="portfolio-section" aria-labelledby="portfolio-title">
          <div className="max-w-6xl mx-auto px-6 md:px-12 lg:px-24">
            <h2
              id="portfolio-title"
              className="font-heading text-3xl sm:text-4xl font-light text-center mb-4 tracking-tight"
              style={{ color: "#1A231E" }}
              data-testid="portfolio-title"
            >
              Din portofoliul nostru
            </h2>
            <p className="text-base text-center mb-12" style={{ color: "#4A524D" }} data-testid="portfolio-subtitle">
              Câteva dintre evenimentele pe care le-am creat cu drag
            </p>

            <div className="carousel-container relative px-12">
              <Carousel
                opts={{
                  align: "start",
                  loop: true,
                }}
                plugins={[
                  Autoplay({
                    delay: 2500,
                    stopOnInteraction: true,
                    stopOnMouseEnter: true,
                  }),
                ]}
                className="w-full"
                data-testid="portfolio-carousel"
                aria-label="Galerie portofoliu evenimente"
              >
                <CarouselContent className="-ml-4">
                  {portfolioImages.map((image, index) => (
                    <CarouselItem key={index} className="pl-4 basis-full md:basis-1/2 lg:basis-1/3">
                      <article className="overflow-hidden rounded-md border" style={{ borderColor: "#EAE7DF" }}>
                        <div className="aspect-[3/4] overflow-hidden">
                          <img
                            src={image.url}
                            alt={`Eveniment: ${image.title}`}
                            className="portfolio-image w-full h-full object-cover"
                            data-testid={`portfolio-image-${index}`}
                            loading="lazy"
                            width="400"
                            height="533"
                          />
                        </div>
                        <div className="p-4" style={{ backgroundColor: "#FFFFFF" }}>
                          <p
                            className="font-heading text-lg font-normal"
                            style={{ color: "#1A231E" }}
                            data-testid={`portfolio-title-${index}`}
                          >
                            {image.title}
                          </p>
                        </div>
                      </article>
                    </CarouselItem>
                  ))}
                </CarouselContent>
                <CarouselPrevious
                  className="border-[#2D5A27] text-[#2D5A27] hover:bg-[#2D5A27] hover:text-white -left-0"
                  data-testid="carousel-prev"
                  aria-label="Imaginea anterioara"
                />
                <CarouselNext
                  className="border-[#2D5A27] text-[#2D5A27] hover:bg-[#2D5A27] hover:text-white -right-0"
                  data-testid="carousel-next"
                  aria-label="Imaginea urmatoare"
                />
              </Carousel>
            </div>
          </div>
        </section>

        {/* Services Section */}
        <section className="py-16 md:py-24 px-6 md:px-12 lg:px-24" style={{ backgroundColor: "#F1EFE9" }} data-testid="services-section" aria-labelledby="services-title">
          <div className="max-w-6xl mx-auto">
            <h2
              id="services-title"
              className="font-heading text-3xl sm:text-4xl font-light text-center mb-4 tracking-tight"
              style={{ color: "#1A231E" }}
              data-testid="services-title"
            >
              Cum te putem ajuta
            </h2>
            <p className="text-base text-center mb-12" style={{ color: "#4A524D" }} data-testid="services-subtitle">
              Servicii complete pentru evenimente de neuitat
            </p>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 lg:gap-8">
              {services.map((service, index) => (
                <article
                  key={index}
                  className="service-card p-8 lg:p-10 rounded-md border bg-white"
                  style={{ borderColor: "#EAE7DF" }}
                  data-testid={`service-card-${index}`}
                >
                  <div className="mb-4">
                    <ServiceIcon type={service.icon} />
                  </div>
                  <h3
                    className="font-heading text-xl sm:text-2xl font-normal mb-3"
                    style={{ color: "#1A231E" }}
                    data-testid={`service-title-${index}`}
                  >
                    {service.title}
                  </h3>
                  <p className="text-base" style={{ color: "#4A524D" }} data-testid={`service-description-${index}`}>
                    {service.description}
                  </p>
                </article>
              ))}
            </div>
          </div>
        </section>

        {/* Contact Section */}
        <section className="py-16 md:py-24 px-6 md:px-12 lg:px-24" data-testid="contact-section" aria-labelledby="contact-title">
          <div className="max-w-4xl mx-auto text-center">
            <h2
              id="contact-title"
              className="font-heading text-3xl sm:text-4xl font-light mb-4 tracking-tight"
              style={{ color: "#1A231E" }}
              data-testid="contact-title"
            >
              Să păstrăm legătura
            </h2>
            <p className="text-base mb-12" style={{ color: "#4A524D" }} data-testid="contact-subtitle">
              Suntem aici pentru a transforma visele tale în realitate
            </p>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-12">
              {/* Phone */}
              <a
                href="tel:0733941020"
                className="contact-item flex flex-col items-center p-6 rounded-md border bg-white"
                style={{ borderColor: "#EAE7DF", color: "#1A231E" }}
                data-testid="contact-phone"
                aria-label="Sună-ne la 0733 941 020"
              >
                <Phone className="w-6 h-6 mb-3 text-[#2D5A27]" aria-hidden="true" />
                <span className="text-base font-medium">Telefon</span>
                <span className="text-base" style={{ color: "#4A524D" }}>
                  0733 941 020
                </span>
              </a>

              {/* Email */}
              <a
                href="mailto:office@starevents.ro"
                className="contact-item flex flex-col items-center p-6 rounded-md border bg-white"
                style={{ borderColor: "#EAE7DF", color: "#1A231E" }}
                data-testid="contact-email"
                aria-label="Trimite email la office@starevents.ro"
              >
                <Mail className="w-6 h-6 mb-3 text-[#2D5A27]" aria-hidden="true" />
                <span className="text-base font-medium">Email</span>
                <span className="text-base" style={{ color: "#4A524D" }}>
                  office@starevents.ro
                </span>
              </a>

              {/* Address */}
              <address
                className="contact-item flex flex-col items-center p-6 rounded-md border bg-white not-italic"
                style={{ borderColor: "#EAE7DF", color: "#1A231E" }}
                data-testid="contact-address"
              >
                <MapPin className="w-6 h-6 mb-3 text-[#2D5A27]" aria-hidden="true" />
                <span className="text-base font-medium">Adresă</span>
                <span className="text-base text-center" style={{ color: "#4A524D" }}>
                  Calea Unirii nr. 30, Craiova, Dolj
                </span>
              </address>

              {/* Working Hours */}
              <div
                className="contact-item flex flex-col items-center p-6 rounded-md border bg-white"
                style={{ borderColor: "#EAE7DF", color: "#1A231E" }}
                data-testid="contact-hours"
              >
                <Clock className="w-6 h-6 mb-3 text-[#2D5A27]" aria-hidden="true" />
                <span className="text-base font-medium">Program</span>
                <span className="text-base text-center" style={{ color: "#4A524D" }}>
                  L-V: 09:00-18:00
                </span>
                <span className="text-base text-center" style={{ color: "#4A524D" }}>
                  S: 09:00-14:00
                </span>
              </div>
            </div>

            {/* Social Links */}
            <nav className="flex justify-center gap-6" aria-label="Rețele sociale">
              <a
                href="https://www.facebook.com/profile.php?id=100076137455134"
                target="_blank"
                rel="noopener noreferrer"
                className="social-icon p-3 rounded-full border"
                style={{ borderColor: "#EAE7DF", color: "#4A524D" }}
                data-testid="social-facebook"
                aria-label="Urmărește-ne pe Facebook"
              >
                <Facebook className="w-6 h-6" aria-hidden="true" />
              </a>
              <a
                href="https://www.instagram.com/star_events_craiova/"
                target="_blank"
                rel="noopener noreferrer"
                className="social-icon p-3 rounded-full border"
                style={{ borderColor: "#EAE7DF", color: "#4A524D" }}
                data-testid="social-instagram"
                aria-label="Urmărește-ne pe Instagram"
              >
                <Instagram className="w-6 h-6" aria-hidden="true" />
              </a>
            </nav>
          </div>
        </section>
      </main>

      {/* Footer */}
      <footer className="py-8 px-6 md:px-12 lg:px-24 border-t" style={{ borderColor: "#EAE7DF" }} data-testid="footer" role="contentinfo">
        <div className="max-w-6xl mx-auto flex flex-col md:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-2">
            <img
              src={`${process.env.PUBLIC_URL}/images/logo.png`}
              alt="Star Events"
              className="h-10 w-auto"
              width="100"
              height="40"
              loading="lazy"
            />
          </div>
          <p className="text-base" style={{ color: "#4A524D" }} data-testid="footer-copyright">
            {new Date().getFullYear()} Star Events. Toate drepturile rezervate.
          </p>
        </div>
      </footer>
    </div>
  );
};

function App() {
  // Get basename from PUBLIC_URL for subdirectory deployment
  const basename = process.env.PUBLIC_URL || '/';
  
  return (
    <div className="App">
      <BrowserRouter basename={basename}>
        <Routes>
          <Route path="/" element={<LandingPage />} />
        </Routes>
      </BrowserRouter>
    </div>
  );
}

export default App;
